/**
 * Central Configuration for AION BI Frontend
 * 
 * This file contains all centralized configuration values.
 * Only hardcode allowed: ROOT_EMAIL
 */

// ============================================
// ROOT USER (Only hardcode allowed)
// ============================================
export const ROOT_EMAIL = 'leonardo@finnapp.com';

// ============================================
// API Configuration
// ============================================
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:4000',
  TIMEOUT: 30000, // 30 seconds
} as const;

// ============================================
// Google OAuth Configuration
// ============================================
export const GOOGLE_OAUTH_CONFIG = {
  CLIENT_ID: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
} as const;

// ============================================
// Internal Company Configuration
// ============================================
export const INTERNAL_COMPANY = {
  NAME: 'Finnapp',
  SUPPORT_EMAIL: 'finnapps.externo@finnegans.com.ar',
} as const;

// ============================================
// Role Configuration (synchronized with backend)
// ============================================
export const ROLES = {
  ADMIN: 'ADMIN',
  ANALYST: 'ANALYST',
  CLIENT: 'CLIENT',
} as const;

// ============================================
// User Type Configuration (synchronized with backend)
// ============================================
export const USER_TYPES = {
  INTERNAL: 'INTERNAL',
  EXTERNAL: 'EXTERNAL',
} as const;

// ============================================
// User Status Configuration (synchronized with backend)
// ============================================
export const USER_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;

// ============================================
// UI Configuration
// ============================================
export const UI_CONFIG = {
  DEFAULT_PAGE_SIZE: 10,
  TOAST_DURATION: 3000, // 3 seconds
  DEBOUNCE_DELAY: 300, // 300ms
  CHART_COLORS: [
    '#0284c7', // sky-600
    '#38bdf8', // sky-400
    '#f59e0b', // amber-500
    '#ef4444', // red-500
    '#8b5cf6', // violet-500
    '#ec4899', // pink-500
    '#06b6d4', // cyan-500
    '#f97316', // orange-500
  ],
} as const;

// ============================================
// Validation
// ============================================
export const VALIDATION = {
  PASSWORD_MIN_LENGTH: 8,
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
} as const;

// ============================================
// Local Storage Keys
// ============================================
export const STORAGE_KEYS = {
  TOKEN: 'token',
  USER: 'user',
  THEME: 'theme',
  SELECTED_COMPANY: 'selectedCompanyId',
} as const;

// ============================================
// Route Paths
// ============================================
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  DASHBOARD: '/dashboard',
  COMPANIES: '/empresas',
  USERS: '/usuarios',
  DATASOURCES: '/datasources',
  ACTIVITY: '/actividad',
  ONBOARDING: '/onboarding',
  AUTH_CALLBACK: '/auth/callback',
} as const;

// ============================================
// Helper Functions
// ============================================

/**
 * Check if email is ROOT user
 */
export function isRootEmail(email: string): boolean {
  return email.toLowerCase().trim() === ROOT_EMAIL.toLowerCase();
}

/**
 * Check if user belongs to internal company (Finnapp)
 */
export function isInternalCompany(companyName: string | null | undefined): boolean {
  if (!companyName) return false;
  return companyName.toLowerCase().trim() === INTERNAL_COMPANY.NAME.toLowerCase();
}

/**
 * Get API endpoint URL
 */
export function getApiUrl(path?: string): string {
  return path 
    ? `${API_CONFIG.BASE_URL}${path.startsWith('/') ? path : '/' + path}`
    : API_CONFIG.BASE_URL;
}

/**
 * Format role name for display (replaces legacy "Controller General" references)
 */
export function formatRoleName(role: string): string {
  const roleMap: Record<string, string> = {
    [ROLES.ADMIN]: 'Administrador',
    [ROLES.ANALYST]: 'Analista',
    [ROLES.CLIENT]: 'Cliente',
  };
  return roleMap[role] || role;
}

/**
 * Format user type for display
 */
export function formatUserType(type: string): string {
  const typeMap: Record<string, string> = {
    [USER_TYPES.INTERNAL]: 'Interno',
    [USER_TYPES.EXTERNAL]: 'Externo',
  };
  return typeMap[type] || type;
}

export default {
  ROOT_EMAIL,
  API_CONFIG,
  GOOGLE_OAUTH_CONFIG,
  INTERNAL_COMPANY,
  ROLES,
  USER_TYPES,
  USER_STATUS,
  UI_CONFIG,
  VALIDATION,
  STORAGE_KEYS,
  ROUTES,
  isRootEmail,
  isInternalCompany,
  getApiUrl,
  formatRoleName,
  formatUserType,
};
